import React, { FC } from "react";

import { Box, Button, Link, Stack, Typography } from "@mui/material";

import StackContent from "@components/StackContent";
import { Colors } from "@theme/colors";
import { addParameters, consoleSignupURL } from "@src/urls";
import { UtmParameters } from "@src/helpers/utms";

import ProductHuntButton from "../ProductHunt";
import { ChevronRightRounded } from "@mui/icons-material";

interface Props {
  productHuntUrl?: string;
  productHuntImage?: string;
  productHuntAlt?: string;

  ctaType?: "button" | "text";

  background?: "gray" | "white";
  stackMargin?: boolean;
  ctaText?: string;
  ctaUrl?: string;
  ctaUtms?: UtmParameters;

  pangolin?: boolean;
  pangolinUrl?: string;

  sectionTitle?: string;

  message?: string;
}

const TryPangea: FC<Props> = ({
  productHuntUrl,
  productHuntImage,
  productHuntAlt,
  ctaType,
  background = "gray",
  ctaText = "Start Free",
  ctaUrl = consoleSignupURL,
  ctaUtms = {},
  stackMargin = true,
  pangolin = false,
  pangolinUrl = "#",
  sectionTitle = "Try Pangea for free",
  message = "Pangea's APIs are easy to integrate and free to start with. Sign up today and add security to your app!",
}) => {
  const calculatedCtaUrl = addParameters(ctaUrl, ctaUtms);

  return (
    <StackContent stackMargin={stackMargin}>
      <Stack
        alignItems="center"
        gap={2}
        maxWidth="1080px"
        sx={{
          margin: { xs: "-40px -24px 0", md: "0" },
          padding: "40px 20px",
          backgroundColor:
            background === "gray" ? Colors.BlueGrey : Colors.PrimaryContrast,
          borderRadius: { xs: "0", md: "30px" },
        }}
      >
        <Typography variant="h2">{sectionTitle}</Typography>
        <Typography
          variant="h6"
          sx={{
            maxWidth: "520px",
            textAlign: "center",
            color: Colors.TextPrimary,
          }}
        >
          {message}
        </Typography>
        <Box
          sx={{
            width:
              pangolin === true
                ? {
                    xs: "100%",
                    md: "auto",
                  }
                : "auto",
            textAlign: "center",

            "&:hover": {
              ".ee-pangolin": {
                transform: "translate3d(40px, 0, 0) scale3d(0.5, 0.5, 0.5)",
                img: {
                  transform: "rotate(0deg)",
                },
              },
            },
            "@media (max-width: 900px)": {
              "&:active": {
                ".ee-pangolin": {
                  transform: "translate3d(40px, 0, 0) scale3d(0.5, 0.5, 0.5)",
                  img: {
                    transform: "rotate(0deg)",
                  },
                },
              },
            },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              display: pangolin === true ? "inline-block" : "flex",
            }}
            gap={2}
          >
            <Box
              sx={{
                position: "relative",
                width: "auto",
                zIndex: 0,
              }}
            >
              <Link
                href={calculatedCtaUrl}
                target="pangea_console"
                underline="none"
                data-testid="try-pangea-cta"
                sx={{
                  zIndex: 5,
                }}
              >
                {ctaType !== "text" ? (
                  <Button
                    disableRipple
                    disableElevation
                    variant="contained"
                    size="large"
                    sx={{
                      color: Colors.PrimaryContrast,
                      margin: { xs: "0 auto", md: "inherit" },
                      height: "50px",
                      minWidth: "230px",
                      borderRadius: "8px",
                    }}
                  >
                    {ctaText}
                  </Button>
                ) : (
                  <Box>
                    <Button
                      disableRipple
                      disableElevation
                      variant="text"
                      size="large"
                      sx={{
                        fontColor: Colors.PrimaryContrast,
                        fontSize: "18px",
                      }}
                    >
                      {ctaText}{" "}
                      <ChevronRightRounded sx={{ fontSize: "24px" }} />
                    </Button>
                  </Box>
                )}
              </Link>

              {pangolin === true && pangolinUrl !== "" ? (
                <Link
                  href={pangolinUrl}
                  target="_blank"
                  sx={{
                    position: "absolute",
                    top: "-77px",
                    right: "-90px",
                    zIndex: -1,
                    transition: "transform 300ms ease-in-out",
                    transformOrigin: "center",
                    transform: "translate3d(0, 0, 0) scale3d(0.25, 0.25, 0.25)",
                  }}
                  className="ee-pangolin"
                >
                  <Box
                    component="img"
                    src="/ee-pangolin-right.png"
                    sx={{
                      transition: "transform 300ms ease-in-out",
                      transform: "rotate(-90deg)",
                    }}
                  />
                </Link>
              ) : (
                ""
              )}
            </Box>

            {productHuntUrl && productHuntImage && productHuntAlt && (
              <ProductHuntButton
                linkUrl={productHuntUrl}
                imageUrl={productHuntImage}
                altText={productHuntAlt}
              />
            )}
            {/* <RedactFeatured /> */}
          </Stack>
        </Box>
      </Stack>
    </StackContent>
  );
};

export default TryPangea;
